export const SEO_COLLECTION = 'SiteSeo'

export const SEO_DOCUMENTS = Object.freeze({
  global: 'global',
  pages: 'pages',
  scripts: 'scripts',
  entitiesServices: 'entities-services',
  entitiesProducts: 'entities-products',
  entitiesArticles: 'entities-articles'
})

export const SEO_ENTITY_DOCUMENTS = Object.freeze({
  services: SEO_DOCUMENTS.entitiesServices,
  products: SEO_DOCUMENTS.entitiesProducts,
  articles: SEO_DOCUMENTS.entitiesArticles
})

export const SEO_DOCUMENT_IDS = Object.freeze(Object.values(SEO_DOCUMENTS))

export const SEO_STORAGE_ROOT = 'seo'
export const SEO_IMAGE_MAX_SIZE_BYTES = 5 * 1024 * 1024
export const SEO_TITLE_LIMIT = 60
export const SEO_DESCRIPTION_LIMIT = 160

export const SEO_LOCALES = Object.freeze(['ua', 'en', 'ru'])

export const SEO_LOCALE_LABELS = Object.freeze({
  ua: 'UA',
  en: 'EN',
  ru: 'RU'
})

export const SEO_PAGES = Object.freeze([
  { key: 'home', label: 'Главная', path: '/', template: false },
  { key: 'about', label: 'О нас', path: '/about', template: false },
  { key: 'services', label: 'Услуги', path: '/services', template: false },
  { key: 'prices', label: 'Прайс', path: '/prices', template: false },
  { key: 'shop', label: 'Магазин', path: '/shop', template: false },
  { key: 'cart', label: 'Корзина', path: '/cart', template: false },
  { key: 'blog', label: 'Блог', path: '/blog', template: false },
  { key: 'contacts', label: 'Контакты', path: '/contacts', template: false },
  { key: 'serviceDetail', label: 'Карточка услуги (шаблон)', path: '/services/:id', template: true, collectionKey: 'services' },
  { key: 'productDetail', label: 'Карточка товара (шаблон)', path: '/products/:id', template: true, collectionKey: 'products' },
  { key: 'articleDetail', label: 'Карточка статьи (шаблон)', path: '(в разработке)', template: true, collectionKey: 'articles' }
])

export const SEO_PAGE_KEYS = Object.freeze(SEO_PAGES.map(page => page.key))

export const SEO_ENTITY_SECTIONS = Object.freeze([
  { collectionKey: 'services', label: 'Услуги', single: 'Услуга', pageKey: 'serviceDetail' },
  { collectionKey: 'products', label: 'Товары', single: 'Товар', pageKey: 'productDetail' },
  { collectionKey: 'articles', label: 'Статьи', single: 'Статья', pageKey: 'articleDetail' }
])

export const SEO_PLACEHOLDERS = Object.freeze([
  { token: '{title}', label: 'Название услуги / товара / статьи' },
  { token: '{category}', label: 'Название первой категории сущности' },
  { token: '{excerpt}', label: 'Краткий текст (text / summaryText)' },
  { token: '{price}', label: 'Цена (только для товаров)' },
  { token: '{siteName}', label: 'Название сайта из общих настроек' }
])

export const SEO_OG_TYPES = Object.freeze([
  { value: 'website', label: 'website' },
  { value: 'article', label: 'article' },
  { value: 'product', label: 'product' }
])

export const SEO_SCRIPT_PLACEMENTS = Object.freeze([
  { value: 'head', label: '<head>' },
  { value: 'bodyStart', label: 'Начало <body>' },
  { value: 'bodyEnd', label: 'Конец <body>' }
])

export const SEO_SCRIPT_TYPES = Object.freeze([
  { value: 'external', label: 'Внешний файл (src)' },
  { value: 'inline', label: 'Инлайн-код' }
])

export const SEO_SCRIPT_ENVIRONMENTS = Object.freeze([
  { value: 'production', label: 'Production' },
  { value: 'development', label: 'Development' }
])

export const SEO_IMAGE_RECOMMENDATIONS = Object.freeze([
  'Оптимальный размер 1200 × 630 (соотношение 1.91:1), минимум 600 × 315.',
  'Формат JPG или PNG — WebP и AVIF часть мессенджеров не парсит.',
  'Вес до 5 МБ: Telegram и WhatsApp обрезают тяжелые файлы.',
  'Не размещайте текст у самых краев — часть клиентов кропает картинку.'
])

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

export function createLocalized(source) {
  const result = { ua: '', en: '', ru: '' }
  if (typeof source === 'string') {
    result.ua = source
    return result
  }
  if (!isPlainObject(source)) return result
  SEO_LOCALES.forEach(locale => {
    const value = source[locale]
    result[locale] = typeof value === 'string' ? value : ''
  })
  return result
}

export function isEmptyLocalized(value) {
  const localized = createLocalized(value)
  return SEO_LOCALES.every(locale => !localized[locale].trim())
}

export function localizedText(value, locale) {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (!isPlainObject(value)) return ''
  return value[locale] || value.ua || value.en || value.ru || ''
}

export function createSeoImage(source) {
  const raw = isPlainObject(source) ? source : {}
  return {
    url: typeof raw.url === 'string' ? raw.url : '',
    alt: createLocalized(raw.alt),
    width: Number(raw.width) || 0,
    height: Number(raw.height) || 0,
    type: typeof raw.type === 'string' ? raw.type : ''
  }
}

export function createDefaultSeoImage(source) {
  const image = createSeoImage(source)
  if (!image.width) image.width = 1200
  if (!image.height) image.height = 630
  if (!image.type) image.type = 'image/jpeg'
  return image
}

export function createSeoBlock(source) {
  const raw = isPlainObject(source) ? source : {}
  const robots = isPlainObject(raw.robots) ? raw.robots : {}
  return {
    title: createLocalized(raw.title),
    description: createLocalized(raw.description),
    keywords: createLocalized(raw.keywords),
    image: createSeoImage(raw.image),
    ogType: typeof raw.ogType === 'string' ? raw.ogType : '',
    canonicalPath: typeof raw.canonicalPath === 'string' ? raw.canonicalPath : '',
    robots: {
      index: robots.index !== false,
      follow: robots.follow !== false
    },
    useTitleTemplate: raw.useTitleTemplate !== false,
    structuredData: typeof raw.structuredData === 'string' ? raw.structuredData : ''
  }
}

export function isEmptySeoBlock(block) {
  const seoBlock = createSeoBlock(block)
  return isEmptyLocalized(seoBlock.title)
    && isEmptyLocalized(seoBlock.description)
    && isEmptyLocalized(seoBlock.keywords)
    && !seoBlock.image.url.trim()
    && isEmptyLocalized(seoBlock.image.alt)
    && !seoBlock.ogType.trim()
    && !seoBlock.canonicalPath.trim()
    && !seoBlock.structuredData.trim()
    && seoBlock.robots.index
    && seoBlock.robots.follow
    && seoBlock.useTitleTemplate
}

export function createGlobalSeo(source) {
  const raw = isPlainObject(source) ? source : {}
  const robots = isPlainObject(raw.robots) ? raw.robots : {}
  const twitter = isPlainObject(raw.twitter) ? raw.twitter : {}
  const verification = isPlainObject(raw.verification) ? raw.verification : {}
  const ogLocales = isPlainObject(raw.ogLocales) ? raw.ogLocales : {}

  return {
    siteUrl: typeof raw.siteUrl === 'string' ? raw.siteUrl : '',
    siteName: createLocalized(raw.siteName),
    titleTemplate: createLocalized(isPlainObject(raw.titleTemplate) || typeof raw.titleTemplate === 'string'
      ? raw.titleTemplate
      : { ua: '{title} | {siteName}', en: '{title} | {siteName}', ru: '{title} | {siteName}' }),
    defaultTitle: createLocalized(raw.defaultTitle),
    defaultDescription: createLocalized(raw.defaultDescription),
    defaultKeywords: createLocalized(raw.defaultKeywords),
    defaultImage: createDefaultSeoImage(raw.defaultImage),
    ogType: typeof raw.ogType === 'string' && raw.ogType ? raw.ogType : 'website',
    ogLocales: {
      ua: typeof ogLocales.ua === 'string' && ogLocales.ua ? ogLocales.ua : 'uk_UA',
      en: typeof ogLocales.en === 'string' && ogLocales.en ? ogLocales.en : 'en_US',
      ru: typeof ogLocales.ru === 'string' && ogLocales.ru ? ogLocales.ru : 'ru_RU'
    },
    twitter: {
      card: typeof twitter.card === 'string' && twitter.card ? twitter.card : 'summary_large_image',
      site: typeof twitter.site === 'string' ? twitter.site : '',
      creator: typeof twitter.creator === 'string' ? twitter.creator : ''
    },
    robots: {
      index: robots.index !== false,
      follow: robots.follow !== false,
      advanced: typeof robots.advanced === 'string' ? robots.advanced : 'max-image-preview:large'
    },
    verification: {
      google: typeof verification.google === 'string' ? verification.google : '',
      bing: typeof verification.bing === 'string' ? verification.bing : '',
      facebookDomain: typeof verification.facebookDomain === 'string' ? verification.facebookDomain : '',
      pinterest: typeof verification.pinterest === 'string' ? verification.pinterest : ''
    },
    themeColor: typeof raw.themeColor === 'string' && raw.themeColor ? raw.themeColor : '#0f7594',
    structuredData: typeof raw.structuredData === 'string' ? raw.structuredData : '',
    version: Number(raw.version) || 1
  }
}

export function createSeoPagesMap(source) {
  const raw = isPlainObject(source) ? source : {}
  const pages = {}
  SEO_PAGE_KEYS.forEach(pageKey => {
    pages[pageKey] = createSeoBlock(raw[pageKey])
  })
  return pages
}

export function createSeoScript(source) {
  const raw = isPlainObject(source) ? source : {}
  const attributes = isPlainObject(raw.attributes) ? raw.attributes : {}
  const placement = SEO_SCRIPT_PLACEMENTS.some(item => item.value === raw.placement) ? raw.placement : 'head'
  const type = raw.type === 'inline' ? 'inline' : 'external'
  const environments = Array.isArray(raw.environments)
    ? raw.environments.filter(value => SEO_SCRIPT_ENVIRONMENTS.some(item => item.value === value))
    : []

  return {
    id: typeof raw.id === 'string' ? raw.id : '',
    name: typeof raw.name === 'string' ? raw.name : '',
    enabled: raw.enabled !== false,
    placement,
    type,
    src: typeof raw.src === 'string' ? raw.src : '',
    code: typeof raw.code === 'string' ? raw.code : '',
    noscript: typeof raw.noscript === 'string' ? raw.noscript : '',
    attributes: {
      async: attributes.async === true,
      defer: attributes.defer === true,
      id: typeof attributes.id === 'string' ? attributes.id : '',
      type: typeof attributes.type === 'string' ? attributes.type : ''
    },
    dataAttributes: (Array.isArray(raw.dataAttributes) ? raw.dataAttributes : [])
      .filter(isPlainObject)
      .map(item => ({
        key: typeof item.key === 'string' ? item.key : '',
        value: typeof item.value === 'string' ? item.value : ''
      })),
    pages: (Array.isArray(raw.pages) ? raw.pages : []).filter(pageKey => SEO_PAGE_KEYS.includes(pageKey)),
    environments: environments.length ? environments : ['production'],
    order: Number.isFinite(Number(raw.order)) ? Number(raw.order) : 0
  }
}

export function createSeoScriptsList(source) {
  const items = Array.isArray(source) ? source : []
  return items.map(createSeoScript).sort((left, right) => left.order - right.order)
}

export function createEntitySeoMap(source) {
  const raw = isPlainObject(source) ? source : {}
  const items = {}
  Object.keys(raw).forEach(entityId => {
    if (!isPlainObject(raw[entityId])) return
    items[entityId] = createSeoBlock(raw[entityId])
  })
  return items
}

export function createEmptySeoState() {
  return {
    global: createGlobalSeo(),
    pages: createSeoPagesMap(),
    scripts: [],
    entities: {
      services: {},
      products: {},
      articles: {}
    }
  }
}

const SCRIPT_TAG_PATTERN = /<script\b([^>]*)>([\s\S]*?)<\/script\s*>/gi
const NOSCRIPT_TAG_PATTERN = /<noscript\b[^>]*>([\s\S]*?)<\/noscript\s*>/gi
const ATTRIBUTE_PATTERN = /([\w:.-]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g

function parseScriptAttributes(rawAttributes) {
  const attributes = {}
  let match = ATTRIBUTE_PATTERN.exec(rawAttributes)
  while (match) {
    const name = match[1].toLowerCase()
    const value = match[2] !== undefined ? match[2] : (match[3] !== undefined ? match[3] : (match[4] !== undefined ? match[4] : ''))
    attributes[name] = value
    match = ATTRIBUTE_PATTERN.exec(rawAttributes)
  }
  ATTRIBUTE_PATTERN.lastIndex = 0
  return attributes
}

/**
 * Разбирает вставленный сниппет: вырезает внешние теги <script>, переносит
 * async / defer / src / id / type в отдельные поля и вытаскивает <noscript>.
 * Возвращает { changed, error, patch } — patch применяется к скрипту.
 */
export function extractScriptSnippet(rawValue) {
  const source = typeof rawValue === 'string' ? rawValue : ''
  if (!source.includes('<script')) return { changed: false, error: '', patch: {} }

  const scripts = []
  let match = SCRIPT_TAG_PATTERN.exec(source)
  while (match) {
    scripts.push({ attributes: parseScriptAttributes(match[1] || ''), code: match[2] || '', raw: match[0] })
    match = SCRIPT_TAG_PATTERN.exec(source)
  }
  SCRIPT_TAG_PATTERN.lastIndex = 0

  if (!scripts.length) {
    return {
      changed: false,
      error: 'Не удалось разобрать сниппет: тег <script> открыт, но не закрыт. Уберите теги вручную и вставьте только JS-код.',
      patch: {}
    }
  }
  if (scripts.length > 1) {
    return {
      changed: false,
      error: 'В сниппете больше одного тега <script>. Добавьте каждый скрипт отдельной записью.',
      patch: {}
    }
  }

  const noscripts = []
  let noscriptMatch = NOSCRIPT_TAG_PATTERN.exec(source)
  while (noscriptMatch) {
    noscripts.push({ inner: noscriptMatch[1] || '', raw: noscriptMatch[0] })
    noscriptMatch = NOSCRIPT_TAG_PATTERN.exec(source)
  }
  NOSCRIPT_TAG_PATTERN.lastIndex = 0

  let rest = source.replace(scripts[0].raw, '')
  noscripts.forEach(item => {
    rest = rest.replace(item.raw, '')
  })
  if (rest.trim()) {
    return {
      changed: false,
      error: 'В сниппете есть HTML вне тегов <script> и <noscript>. Оставьте только скрипт — админка сама соберет тег на сайте.',
      patch: {}
    }
  }

  const attributes = scripts[0].attributes
  const patch = {
    code: scripts[0].code.trim(),
    attributes: {
      async: Object.prototype.hasOwnProperty.call(attributes, 'async'),
      defer: Object.prototype.hasOwnProperty.call(attributes, 'defer'),
      id: attributes.id || '',
      type: attributes.type && attributes.type !== 'text/javascript' ? attributes.type : ''
    }
  }
  if (attributes.src) {
    patch.src = attributes.src
    patch.type = 'external'
  }
  if (noscripts.length) patch.noscript = noscripts.map(item => item.inner.trim()).join('\n')

  return { changed: true, error: '', patch }
}

export function scriptContainsScriptTag(value) {
  return typeof value === 'string' && /<\s*\/?\s*script\b/i.test(value)
}

export function isValidSiteUrl(value) {
  const source = String(value || '').trim()
  if (!source) return false
  if (source.endsWith('/')) return false
  try {
    const url = new URL(source)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
    return url.pathname === '/' && !url.search && !url.hash
  } catch (error) {
    return false
  }
}

export function siteUrlError(value) {
  const source = String(value || '').trim()
  if (!source) return 'Укажите адрес сайта — без него нельзя собрать абсолютные og:url и og:image.'
  if (source.endsWith('/')) return 'Уберите завершающий слэш: правильный формат https://example.com'
  if (!isValidSiteUrl(source)) return 'Нужен абсолютный URL вида https://example.com, без пути и параметров.'
  return ''
}

export function structuredDataError(value) {
  const source = String(value || '').trim()
  if (!source) return ''
  try {
    JSON.parse(source)
    return ''
  } catch (error) {
    return `Некорректный JSON-LD: ${error.message}`
  }
}

export function applyPlaceholders(value, replacements) {
  let result = String(value || '')
  Object.entries(replacements || {}).forEach(([token, replacement]) => {
    result = result.split(`{${token}}`).join(String(replacement === undefined || replacement === null ? '' : replacement))
  })
  return result
}

export function applyTitleTemplate(template, title, siteName) {
  const source = String(template || '').trim()
  if (!source) return title
  return applyPlaceholders(source, { title, siteName }).replace(/\s{2,}/g, ' ').trim()
}

function pickLocalized(locale, ...values) {
  const found = values.find(value => value && localizedText(value, locale).trim())
  return found ? localizedText(found, locale).trim() : ''
}

function pickImage(...images) {
  const found = (images || []).find(image => image && typeof image.url === 'string' && image.url.trim())
  return found ? createSeoImage(found) : createSeoImage()
}

function normalizePath(path) {
  const source = String(path || '').trim()
  if (!source) return '/'
  return source.startsWith('/') ? source : `/${source}`
}

/**
 * Считает итоговые мета-теги по той же логике наследования, что и клиент:
 * значение сущности -> значение страницы -> global.
 */
export function resolveSeoMeta(options = {}) {
  const global = createGlobalSeo(options.global)
  const page = options.page ? createSeoBlock(options.page) : null
  const entity = options.entity ? createSeoBlock(options.entity) : null
  const locale = SEO_LOCALES.includes(options.locale) ? options.locale : 'ua'
  const replacements = options.replacements || {}
  const siteName = localizedText(global.siteName, locale)

  const rawTitle = pickLocalized(locale, entity && entity.title, page && page.title, global.defaultTitle)
  const rawDescription = pickLocalized(locale, entity && entity.description, page && page.description, global.defaultDescription)
  const rawKeywords = pickLocalized(locale, entity && entity.keywords, page && page.keywords, global.defaultKeywords)

  const title = applyPlaceholders(rawTitle, { ...replacements, siteName })
  const description = applyPlaceholders(rawDescription, { ...replacements, siteName })
  const keywords = applyPlaceholders(rawKeywords, { ...replacements, siteName })

  const useTitleTemplate = !(entity && entity.useTitleTemplate === false)
    && !(page && page.useTitleTemplate === false)

  const image = pickImage(entity && entity.image, page && page.image, global.defaultImage)
  if (!image.url) {
    image.width = global.defaultImage.width
    image.height = global.defaultImage.height
    image.type = global.defaultImage.type
  }
  const imageAlt = pickLocalized(locale, entity && entity.image && entity.image.alt, page && page.image && page.image.alt, global.defaultImage.alt)

  const canonicalPath = (entity && entity.canonicalPath.trim())
    || (page && page.canonicalPath.trim())
    || normalizePath(options.path)

  const robotsSource = [entity, page].find(block => block && (!block.robots.index || !block.robots.follow))
  const robots = robotsSource
    ? { index: robotsSource.robots.index, follow: robotsSource.robots.follow }
    : { index: global.robots.index, follow: global.robots.follow }

  return {
    title: useTitleTemplate ? applyTitleTemplate(localizedText(global.titleTemplate, locale), title, siteName) : title,
    rawTitle: title,
    useTitleTemplate,
    description,
    keywords,
    siteName,
    image: { ...image, altText: imageAlt },
    ogType: (entity && entity.ogType) || (page && page.ogType) || global.ogType || 'website',
    canonicalUrl: `${String(global.siteUrl || '').replace(/\/+$/, '')}${canonicalPath}`,
    canonicalPath,
    robots,
    structuredData: (entity && entity.structuredData.trim()) || (page && page.structuredData.trim()) || global.structuredData.trim()
  }
}

export { clone as cloneSeoValue }
